import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

// AKM POC §5.10 — okuyucu yönü: 'entry' | 'exit' | null (null = kapının yönünden / son hareketten tahmin).
// GenericSelect boş değeri seçili gösteremediği için null formda 'none' gözcüsüyle taşınır.
export const DIRECTION_NONE = 'none';

export const toDirectionValue = (v) => (v === 'entry' || v === 'exit' ? v : null);

export const toAccessPointPayload = (formData) => ({
  ...formData,
  direction: toDirectionValue(formData?.direction),
});

const useAccessPointApi = (queryParams) => {
  const { t } = useTranslation();
  const { user, isSuperAdmin, isEnterpriseAdmin } = useAuth();

  // Purpose dropdown is only shown to tenants that have the canteen module.
  // For superAdmin we always show it (platform-level visibility). Otherwise
  // gate on any CN.* privilege in the user's claims — presence of either
  // Read or Write indicates the tenant has purchased the canteen module.
  const hasCanteenModule = useMemo(() => {
    if (isSuperAdmin) return true;
    const privs = user?.privileges || [];
    return privs.some((p) => p === 'CN.Read' || p === 'CN.Write');
  }, [user, isSuperAdmin]);

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [gates, setGates] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/gates?pageSize=500').then((res) => {
      setGates(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/access-points?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/access-points', toAccessPointPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/access-points/${id}`, toAccessPointPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/access-points/${ids[0]}`);
      } else {
        await api.post('/v1/access-points/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const deviceTypeLabel = useCallback((val) => {
    const map = {
      turnstile: t('accessPoint.turnstile'),
      door: t('accessPoint.door'),
      barrier: t('accessPoint.barrier'),
      qr_point: t('accessPoint.qrPoint'),
    };
    return map[val] || val;
  }, [t]);

  const readerDirectionOptions = useMemo(() => [
    { id: DIRECTION_NONE, name: t('accessPoint.directionNone') },
    { id: 'entry', name: t('accessPoint.directionEntry') },
    { id: 'exit', name: t('accessPoint.directionExit') },
  ], [t]);

  const readerDirectionLabel = useCallback((val) => {
    if (val === 'entry') return t('accessPoint.directionEntry');
    if (val === 'exit') return t('accessPoint.directionExit');
    return t('accessPoint.directionNone');
  }, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('accessPoint.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'gateName', label: t('accessPoint.gateName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'buildingName', label: t('accessPoint.buildingName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'deviceType', label: t('accessPoint.deviceType'), options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['turnstile', 'door', 'barrier', 'qr_point'],
          renderValue: (v) => deviceTypeLabel(v),
        },
        customFilterListOptions: { render: (v) => deviceTypeLabel(v) },
        customBodyRender: (value) => deviceTypeLabel(value),
      },
    },
    { name: 'ipAddress', label: t('accessPoint.ipAddress'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'direction', label: t('accessPoint.direction'), options: {
        filter: false, sort: false,
        customBodyRender: (value) => readerDirectionLabel(value),
      },
    },
  ], [t, deviceTypeLabel, readerDirectionLabel]);

  const deviceTypeOptions = useMemo(() => [
    { id: 'turnstile', name: t('accessPoint.turnstile') },
    { id: 'door', name: t('accessPoint.door') },
    { id: 'barrier', name: t('accessPoint.barrier') },
    { id: 'qr_point', name: t('accessPoint.qrPoint') },
  ], [t]);

  const purposeOptions = useMemo(() => [
    { id: 'gate', name: t('canteen.purposeGate') },
    { id: 'canteen', name: t('canteen.purposeCanteen') },
  ], [t]);

  const objectSchema = useMemo(() => {
    const base = [
      {
        name: 'gateId', label: t('accessPoint.gateName'), type: 'select', grid: 12, required: true,
        options: gates, idKey: 'id', labelKey: 'name',
      },
      { name: 'name', label: t('accessPoint.name'), type: 'text', grid: 12, required: true },
      { name: 'ipAddress', label: t('accessPoint.ipAddress'), type: 'text', grid: 12 },
      {
        name: 'deviceType', label: t('accessPoint.deviceType'), type: 'select', grid: 12, required: true,
        options: deviceTypeOptions, idKey: 'id', labelKey: 'name',
      },
      {
        name: 'direction', label: t('accessPoint.direction'), type: 'select', grid: 12,
        options: readerDirectionOptions, idKey: 'id', labelKey: 'name', defaultValue: DIRECTION_NONE,
      },
    ];
    // Purpose only appears for tenants with the Canteen module purchased —
    // keeps the form unchanged for everyone else.
    if (hasCanteenModule) {
      base.push({
        name: 'purpose', label: t('canteen.accessPointPurpose'), type: 'select', grid: 12,
        options: purposeOptions, idKey: 'id', labelKey: 'name',
        description: t('canteen.accessPointPurposeHint'),
      });
    }
    base.push(
      { name: 'latitude', label: t('accessPoint.latitude'), type: 'number', grid: 6, description: t('accessPoint.latitudeHint') },
      { name: 'longitude', label: t('accessPoint.longitude'), type: 'number', grid: 6, description: t('accessPoint.longitudeHint') },
      { name: 'geoFenceRadiusMeters', label: t('accessPoint.geoFenceRadius'), type: 'number', grid: 12, description: t('accessPoint.geoFenceRadiusHint') },
    );
    return base;
  }, [t, gates, deviceTypeOptions, purposeOptions, hasCanteenModule, readerDirectionOptions]);

  const regenerateQr = useCallback(async (id) => {
    try {
      const res = await api.post(`/v1/access/access-points/${id}/regenerate-qr`);
      setRefreshTrigger((p) => p + 1);
      return res.data?.data?.qrToken || null;
    } catch {
      return null;
    }
  }, []);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete, regenerateQr };
};

export default useAccessPointApi;
